import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { Layout } from './components/layout/Layout';
import { AdminLayout } from './components/layout/Layout';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { CatLoading } from './components/ui/CatLoading';
import { NoInternetPage as OfflinePage } from './pages/NoInternetPage';
import { BackButtonManager } from './components/ui/BackButtonManager';

const HomePage = lazy(() => import('./pages/store/HomePage').then(m => ({ default: m.HomePage })));
const CatsListingPage = lazy(() => import('./pages/store/CatsListingPage').then(m => ({ default: m.CatsListingPage })));
const CatDetailPage = lazy(() => import('./pages/store/CatDetailPage').then(m => ({ default: m.CatDetailPage })));
const ProductsListingPage = lazy(() => import('./pages/store/ProductsListingPage').then(m => ({ default: m.ProductsListingPage })));
const ProductDetailPage = lazy(() => import('./pages/store/ProductDetailPage').then(m => ({ default: m.ProductDetailPage })));
const CartPage = lazy(() => import('./pages/store/CartPage').then(m => ({ default: m.CartPage })));
const FavoritesPage = lazy(() => import('./pages/store/FavoritesPage').then(m => ({ default: m.FavoritesPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/store/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const AboutPage = lazy(() => import('./pages/store/AboutPage').then(m => ({ default: m.AboutPage })));
const TermsPage = lazy(() => import('./pages/store/TermsPage').then(m => ({ default: m.TermsPage })));
const MyRequestsPage = lazy(() => import('./pages/store/MyRequestsPage').then(m => ({ default: m.MyRequestsPage })));
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

const AdminDashboardPage = lazy(() => import('./pages/admin/DashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const AdminCatsPage = lazy(() => import('./pages/admin/AdminCatsPage').then(m => ({ default: m.AdminCatsPage })));
const AdminCatFormPage = lazy(() => import('./pages/admin/AdminCatFormPage').then(m => ({ default: m.AdminCatFormPage })));
const AdminProductsPage = lazy(() => import('./pages/admin/AdminProductsPage').then(m => ({ default: m.AdminProductsPage })));
const AdminProductFormPage = lazy(() => import('./pages/admin/AdminProductFormPage').then(m => ({ default: m.AdminProductFormPage })));
const AdminAdoptionRequestsPage = lazy(() => import('./pages/admin/AdminAdoptionRequestsPage').then(m => ({ default: m.AdminAdoptionRequestsPage })));
const AdminPurchaseRequestsPage = lazy(() => import('./pages/admin/AdminPurchaseRequestsPage').then(m => ({ default: m.AdminPurchaseRequestsPage })));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <CatLoading size="lg" />
    </div>
  );
}

// Suspends only the leaf page behind a loader instead of the entire route tree,
// so lazy-chunk loads can never leave the AnimatePresence transition blank/stalled.
function SuspenseBoundary({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

const SCROLL_POSITIONS_KEY = 'catcare_scroll_positions';

// Scrolls to top on normal navigation, but restores the exact scroll position
// when the user navigates back/forward (so "back" from the cart returns you to
// the exact spot on the cat/product listing you were reading).
function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevKey = useRef(location.key);

  useEffect(() => {
    // Save the scroll position of the page we are leaving.
    if (prevKey.current !== location.key) {
      const leaving = prevKey.current;
      prevKey.current = location.key;
      if (window.scrollY > 0) {
        try {
          const positions = JSON.parse(sessionStorage.getItem(SCROLL_POSITIONS_KEY) || '{}');
          positions[leaving] = window.scrollY;
          sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(positions));
        } catch {
          // ignore quota/parse issues
        }
      }
    }
  }, [location.key]);

  useEffect(() => {
    if (navigationType === 'POP') {
      let y = 0;
      try {
        const positions = JSON.parse(sessionStorage.getItem(SCROLL_POSITIONS_KEY) || '{}');
        y = positions[location.key] ?? 0;
      } catch {
        y = 0;
      }
      requestAnimationFrame(() => window.scrollTo(0, y));
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.key, navigationType]);

  return null;
}

function ContextMenuBlocker() {
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handler, { passive: false });
    return () => document.removeEventListener('contextmenu', handler);
  }, []);
  return null;
}

// Build-mode gating for the native admin app:
//  - undefined (web PWA): full storefront + admin, unchanged.
//  - 'admin'              : login + admin only, never reaches the storefront.
const APP_MODE = import.meta.env.VITE_APP_MODE;

function AnimatedRoutes() {
  const location = useLocation();
  const isAdminApp = APP_MODE === 'admin';

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <Routes location={location}>
          {isAdminApp ? (
            <>
              <Route element={<Layout />}>
                <Route path="/login" element={<SuspenseBoundary><LoginPage /></SuspenseBoundary>} />
                <Route path="/register" element={<SuspenseBoundary><RegisterPage /></SuspenseBoundary>} />
                <Route path="/" element={<Navigate to="/admin" replace />} />
              </Route>
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<SuspenseBoundary><AdminDashboardPage /></SuspenseBoundary>} />
                <Route path="/admin/cats" element={<SuspenseBoundary><AdminCatsPage /></SuspenseBoundary>} />
                <Route path="/admin/cats/new" element={<SuspenseBoundary><AdminCatFormPage /></SuspenseBoundary>} />
                <Route path="/admin/cats/:id/edit" element={<SuspenseBoundary><AdminCatFormPage /></SuspenseBoundary>} />
                <Route path="/admin/products" element={<SuspenseBoundary><AdminProductsPage /></SuspenseBoundary>} />
                <Route path="/admin/products/new" element={<SuspenseBoundary><AdminProductFormPage /></SuspenseBoundary>} />
                <Route path="/admin/products/:id/edit" element={<SuspenseBoundary><AdminProductFormPage /></SuspenseBoundary>} />
                <Route path="/admin/adoption-requests" element={<SuspenseBoundary><AdminAdoptionRequestsPage /></SuspenseBoundary>} />
                <Route path="/admin/purchase-requests" element={<SuspenseBoundary><AdminPurchaseRequestsPage /></SuspenseBoundary>} />
              </Route>
            </>
          ) : (
            <>
              <Route element={<Layout />}>
                <Route path="/" element={<SuspenseBoundary><HomePage /></SuspenseBoundary>} />
                <Route path="/cats" element={<SuspenseBoundary><CatsListingPage /></SuspenseBoundary>} />
                <Route path="/cats/:id" element={<SuspenseBoundary><CatDetailPage /></SuspenseBoundary>} />
                <Route path="/products" element={<SuspenseBoundary><ProductsListingPage /></SuspenseBoundary>} />
                <Route path="/products/:id" element={<SuspenseBoundary><ProductDetailPage /></SuspenseBoundary>} />
                <Route path="/cart" element={<SuspenseBoundary><CartPage /></SuspenseBoundary>} />
                <Route path="/favorites" element={<SuspenseBoundary><FavoritesPage /></SuspenseBoundary>} />
                <Route path="/my-requests" element={<SuspenseBoundary><MyRequestsPage /></SuspenseBoundary>} />
                <Route path="/privacy-policy" element={<SuspenseBoundary><PrivacyPolicyPage /></SuspenseBoundary>} />
                <Route path="/about" element={<SuspenseBoundary><AboutPage /></SuspenseBoundary>} />
                <Route path="/terms" element={<SuspenseBoundary><TermsPage /></SuspenseBoundary>} />
                <Route path="/login" element={<SuspenseBoundary><LoginPage /></SuspenseBoundary>} />
                <Route path="/register" element={<SuspenseBoundary><RegisterPage /></SuspenseBoundary>} />
              </Route>
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<SuspenseBoundary><AdminDashboardPage /></SuspenseBoundary>} />
                <Route path="/admin/cats" element={<SuspenseBoundary><AdminCatsPage /></SuspenseBoundary>} />
                <Route path="/admin/cats/new" element={<SuspenseBoundary><AdminCatFormPage /></SuspenseBoundary>} />
                <Route path="/admin/cats/:id/edit" element={<SuspenseBoundary><AdminCatFormPage /></SuspenseBoundary>} />
                <Route path="/admin/products" element={<SuspenseBoundary><AdminProductsPage /></SuspenseBoundary>} />
                <Route path="/admin/products/new" element={<SuspenseBoundary><AdminProductFormPage /></SuspenseBoundary>} />
                <Route path="/admin/products/:id/edit" element={<SuspenseBoundary><AdminProductFormPage /></SuspenseBoundary>} />
                <Route path="/admin/adoption-requests" element={<SuspenseBoundary><AdminAdoptionRequestsPage /></SuspenseBoundary>} />
                <Route path="/admin/purchase-requests" element={<SuspenseBoundary><AdminPurchaseRequestsPage /></SuspenseBoundary>} />
              </Route>
            </>
          )}
          <Route path="/offline" element={<OfflinePage />} />
          <Route path="*" element={<SuspenseBoundary><NotFoundPage /></SuspenseBoundary>} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOnline) {
    return <OfflinePage />;
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ScrollManager />
          <BackButtonManager />
          <ContextMenuBlocker />
          <AnimatedRoutes />
        </BrowserRouter>
        <Toaster
          position="top-center"
          richColors
          closeButton
          duration={3000}
          visibleToasts={3}
        />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
